import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { Location, NgOptimizedImage } from '@angular/common';
import { FrontpageAlertData, Toast } from '@mwe/models';
import { FeatureFrontpageModule } from '@mwe/features/frontpage';
import { FooterComponent } from '../../layout/footer/footer.component';
import { IkpStaticContentService } from '../../service/static-content/ikp-static-content.service';
import { ROUTE_LOGOUT_SUCCESS } from '../../app.constants';
import { HeaderLogoComponent } from '@shared-ikp/layout/header/header-logo/header-logo.component';
import { IkpAuthService } from '@shared-ikp/service/auth/ikp-auth.service';
import { fullRoutePrefix } from '@shared-ikp/app.constants';
import { toSignal } from '@angular/core/rxjs-interop';
import dayjs from 'dayjs';
import { InlineNotificationComponent, UiComponentsModule } from '@mwe/ui/components';
import { ActivatedRoute } from '@angular/router';
import { map } from 'rxjs';
import { ToastService } from '@mwe/services';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ikp-frontpage',
  standalone: true,
  imports: [
    FeatureFrontpageModule,
    HeaderLogoComponent,
    FooterComponent,
    NgOptimizedImage,
    UiComponentsModule,
    InlineNotificationComponent,
  ],
  templateUrl: './frontpage.component.html',
  styleUrl: './frontpage.component.scss',
})
export class FrontpageComponent implements OnInit {
  authService = inject(IkpAuthService);
  staticContent = inject(IkpStaticContentService);
  location = inject(Location);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  showAuthServerAlert = false;
  authServerAlertData: FrontpageAlertData;
  temporaryAlert = toSignal(this.staticContent.getTemporaryAlert());
  showTemporaryAlert = computed(() => this.isTemporaryInfoAlertValid(this.temporaryAlert()));
  readonly queryParamEmail = toSignal(this.route.queryParams.pipe(map(params => params['email'])));
  readonly currentToast = computed<Toast>(() => this.toastService.visibleToasts()[0] ?? null);
  readonly textWithEmail = signal<string>('frontpage.emailHint.message');

  private readonly emailChangeEffect = effect(() => {
    const email = this.queryParamEmail();
    if (email) {
      this.translate.get('frontpage.emailHint.message', { email }).subscribe(translated => {
        this.textWithEmail.set(translated);
      });
    }
  });

  isLoaded = false;

  ngOnInit(): void {
    if (window.location.hash.indexOf('logout=success') !== -1) {
      this.authServerAlertData = {
        type: 'success',
        title: 'frontpage.logoutSuccess.title',
        message: 'frontpage.logoutSuccess.message',
      };
      this.showAuthServerAlert = true;
      this.deleteHashFromBaseUrl();
    }
    this.isLoaded = true;
  }

  private deleteHashFromBaseUrl() {
    const newUrl = window.location.href.replace(fullRoutePrefix(ROUTE_LOGOUT_SUCCESS), '');
    this.location.replaceState(newUrl);
  }

  private isTemporaryInfoAlertValid(temporaryInfoAlert: FrontpageAlertData) {
    if (temporaryInfoAlert) {
      const validFrom = dayjs(temporaryInfoAlert.validFrom);
      const validTo = dayjs(temporaryInfoAlert.validTo);
      if (validFrom.isBefore(dayjs()) && validTo.isAfter(dayjs())) {
        return true;
      }
    }
    return false;
  }

  login() {
    this.authService.login(this.queryParamEmail());
  }

  register() {
    this.authService.register(this.queryParamEmail());
  }

  copyEmailToClipboard() {
    const email = this.queryParamEmail();
    if (!email) {
      return;
    }
    const newToast: Toast = { type: 'success', title: 'frontpage.emailHint.copyToastMessage' };
    navigator.clipboard.writeText(email).then(() => this.toastService.publishNewToast(newToast));
  }
}
